import { Button } from '@viastud/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@viastud/ui/dialog'
import { SubscriptionUserCard } from '@viastud/ui/shared/subscription-user-card'
import type React from 'react'
import { useState } from 'react'

import type { Child } from '@/presenters/parent-subscription.presenter.ts'

interface Props {
  children: Child[]
  onCancel: (id: string) => void
  onRenew: (id: string) => void
  isActionLoading: boolean
  selectedChild: Child | null
  setSelectedChild: (child: Child | null) => void
}

export const ChildSubscriptionList: React.FC<Props> = ({
  children,
  onCancel,
  onRenew,
  isActionLoading,
  selectedChild,
  setSelectedChild,
}) => {
  const [actionType, setActionType] = useState<'cancel' | 'renew' | null>(null)

  const handleOpenModal = (child: Child, type: 'cancel' | 'renew') => {
    setSelectedChild(child)
    setActionType(type)
  }

  const handleConfirm = () => {
    if (!selectedChild || !actionType) return

    if (actionType === 'cancel') {
      onCancel(selectedChild.id)
    } else {
      onRenew(selectedChild.id)
    }
    setSelectedChild(null)
    setActionType(null)
  }

  return (
    <div className="">
      <h2 className="mb-8 text-2xl font-bold text-gray-900">Mes enfants</h2>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {children.map((child) => (
          <SubscriptionUserCard
            key={child.id}
            child={{ ...child, hasParent: false }}
            onCancel={() => {
              handleOpenModal(child, 'cancel')
            }}
            onRenew={() => {
              handleOpenModal(child, 'renew')
            }}
            onRePay={() => {}}
            isActionLoading={isActionLoading}
          />
        ))}
      </div>

      <Dialog
        open={selectedChild !== null && actionType !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedChild(null)
            setActionType(null)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'cancel' ? "Annuler l'abonnement" : "Réactiver l'abonnement"}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'cancel'
                ? `Êtes-vous sûr de vouloir annuler l'abonnement de ${selectedChild?.firstName} ? Il restera actif jusqu'à la fin de la période en cours.`
                : `Voulez-vous réactiver l'abonnement de ${selectedChild?.firstName} ?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSelectedChild(null)
                setActionType(null)
              }}
            >
              Annuler
            </Button>
            <Button
              variant={actionType === 'cancel' ? 'destructive' : 'default'}
              onClick={handleConfirm}
              disabled={isActionLoading}
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
